import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useInView, animate } from "framer-motion";
import { ArrowUpRight, ArrowRight, Recycle, BatteryCharging, Flame, Droplets, Sprout, Mountain, Building2, Cog, Factory, Layers } from "lucide-react";
import Marquee from "@/components/Marquee";
import NetZeroClock from "@/components/NetZeroClock";
import { Reveal, Eyebrow } from "@/components/Reveal";

const heroLine = {
  hidden: { y: "115%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1, delay: 0.35 + i * 0.14, ease: [0.16, 1, 0.3, 1] },
  }),
};

const Counter = ({ to, suffix = "" }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, to, {
      duration: 1.8,
      ease: [0.16, 1, 0.3, 1],
      onUpdate: (v) => setVal(Math.round(v)),
    });
    return () => controls.stop();
  }, [inView, to]);
  return (
    <span ref={ref} className="font-display font-extrabold text-4xl sm:text-5xl text-white">
      {val}
      <span className="text-lime">{suffix}</span>
    </span>
  );
};

const PILLARS = [
  { icon: Recycle, title: "Steel & Metals Recycling", desc: "Scrap and ore to finished carbon, alloy & stainless steel; aluminium and copper recycling.", span: "md:col-span-4 md:row-span-2", testid: "pillar-steel" },
  { icon: BatteryCharging, title: "Energy Storage", desc: "Thermal, compressed air, liquid air & CO2 batteries.", span: "md:col-span-2", testid: "pillar-storage" },
  { icon: Flame, title: "Waste to Energy", desc: "MSW, tyres & biomass to biogas, RNG, CBG, syngas & SAF.", span: "md:col-span-2", testid: "pillar-waste-energy" },
  { icon: Droplets, title: "Air & Water Treatment", desc: "Fume extraction, bag filters, ETP / STP / ZLD plants.", span: "md:col-span-2", testid: "pillar-air-water" },
  { icon: Sprout, title: "Carbon Sequestration & NASA Superfoods", desc: "NASA-recommended spirulina & chlorella, biogenic carbon, biochar.", span: "md:col-span-2", testid: "pillar-algae" },
  { icon: Mountain, title: "Mine Restoration", desc: "EOL mines reborn as solar, wind & farmland.", span: "md:col-span-2", testid: "pillar-mining" },
  { icon: Building2, title: "Green Building", desc: "Green cement, bricks & blocks, and engineered cast profiles & sections from recovered materials.", span: "md:col-span-6", testid: "pillar-green-building" },
];

const PROCESS = ["Concept", "Engineering", "Installation", "Commissioning", "Operation"];

const CAPACITY = [
  { icon: Cog, title: "Medium-Scale Plants", desc: "Modular process islands for rapid deployment and phased investment.", testid: "capacity-medium" },
  { icon: Factory, title: "Large-Scale Plants", desc: "Complete turnkey industrial plants engineered for continuous operation.", testid: "capacity-large" },
  { icon: Layers, title: "Integrated Complexes", desc: "Several green technologies combined on one circular-economy site.", testid: "capacity-integrated" },
];

const Home = () => {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0.2]);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative min-h-screen flex items-end overflow-hidden grain">
        <motion.div style={{ y: bgY }} className="absolute inset-0 -z-10">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(132,204,22,0.14),transparent_55%),radial-gradient(ellipse_at_bottom_left,rgba(16,185,129,0.12),transparent_50%),radial-gradient(ellipse_at_center,rgba(6,182,212,0.06),transparent_60%)]" />
          <div
            className="absolute inset-0 opacity-[0.06]"
            style={{
              backgroundImage:
                "linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)",
              backgroundSize: "72px 72px",
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-obsidian/40 via-transparent to-obsidian" />
        </motion.div>

        <motion.div style={{ opacity: fade }} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20 sm:pb-28 pt-40 w-full">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.8 }}
            className="font-mono text-[11px] sm:text-xs uppercase tracking-[0.35em] text-lime mb-6 flex items-center gap-3"
          >
            <span className="pulse-dot inline-block h-2 w-2 rounded-full bg-lime" />
            Circular Economy · Clean Energy · Net Zero
          </motion.p>

          <h1 className="font-display font-extrabold tracking-tight leading-[0.95] text-[11vw] sm:text-7xl lg:text-8xl">
            <span className="mask-line">
              <motion.span custom={0} variants={heroLine} initial="hidden" animate="show" className="block">
                HEAVY INDUSTRY,
              </motion.span>
            </span>
            <span className="mask-line">
              <motion.span custom={1} variants={heroLine} initial="hidden" animate="show" className="block text-gradient">
                REBORN GREEN.
              </motion.span>
            </span>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.8 }}
            className="mt-6 max-w-xl text-gray-300 text-base sm:text-lg leading-relaxed"
          >
            Green Planet Technologies engineers the circular economy — decarbonisation and clean
            energy technologies that turn waste into steel, power, fuel, building materials,
            clean water and living land. From concept to creation, installation, commissioning
            and operation.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 0.8 }}
            className="mt-8 flex flex-wrap gap-4"
          >
            <Link
              to="/contact"
              data-testid="hero-cta-consultation"
              className="inline-flex items-center gap-2 bg-lime text-obsidian font-semibold px-7 py-3.5 rounded-full hover:bg-lime-bright hover:shadow-[0_0_32px_rgba(132,204,22,0.45)] hover:-translate-y-0.5 transition-all duration-300"
            >
              Request Net Zero Consultation <ArrowUpRight size={18} />
            </Link>
            <Link
              to="/services"
              data-testid="hero-cta-services"
              className="inline-flex items-center gap-2 border border-white/20 text-white font-medium px-7 py-3.5 rounded-full hover:border-lime/60 hover:text-lime transition-all duration-300"
            >
              Explore Technologies <ArrowRight size={18} />
            </Link>
          </motion.div>
        </motion.div>
      </section>

      {/* STATS */}
      <section className="border-b border-white/10 bg-carbon">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 grid grid-cols-2 lg:grid-cols-4 gap-10">
          {[
            { v: 20, s: "+", label: "Green Plant Technologies", testid: "stat-technologies" },
            { v: 7, s: "", label: "Technology Categories", testid: "stat-pillars" },
            { v: 100, s: "%", label: "Net Zero Ambition", testid: "stat-netzero" },
            { v: 1, s: "", label: "Planet. Zero Waste.", testid: "stat-planet" },
          ].map((s) => (
            <div key={s.label} data-testid={s.testid}>
              <Counter to={s.v} suffix={s.s} />
              <p className="mt-2 font-mono text-[11px] uppercase tracking-[0.25em] text-gray-500">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      <Marquee />

      {/* BENTO PILLARS */}
      <section className="py-20 sm:py-28 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <Eyebrow>What we engineer</Eyebrow>
            <h2 className="mt-4 font-display font-bold tracking-tight text-3xl sm:text-4xl lg:text-5xl max-w-3xl">
              Seven categories of <span className="text-gradient">green technology</span>
            </h2>
          </Reveal>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-6 auto-rows-[190px] gap-4">
            {PILLARS.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.06} className={p.span}>
                <Link
                  to="/services"
                  data-testid={p.testid}
                  className="group relative h-full w-full block overflow-hidden rounded-2xl border border-white/10 bg-steel p-6 hover:border-lime/50 hover:shadow-[0_10px_40px_rgba(132,204,22,0.15)] transition-all duration-500"
                >
                  <p.icon
                    className="absolute -bottom-8 -right-8 text-white/[0.05] group-hover:text-lime/15 group-hover:-rotate-6 transition-all duration-700"
                    size={150}
                  />
                  <div className="relative h-full flex flex-col">
                    <p.icon className="text-lime" size={26} />
                    <div className="mt-auto">
                      <h3 className="font-display font-bold text-lg sm:text-xl text-white">{p.title}</h3>
                      <p className="text-sm text-gray-400 mt-1">{p.desc}</p>
                    </div>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="py-20 sm:py-28 bg-carbon border-y border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <Eyebrow>Concept to creation</Eyebrow>
            <h2 className="mt-4 font-display font-bold tracking-tight text-3xl sm:text-4xl">One partner. Every stage.</h2>
          </Reveal>
          <div className="mt-12 grid grid-cols-2 md:grid-cols-5 gap-px bg-white/10 rounded-2xl overflow-hidden border border-white/10">
            {PROCESS.map((step, i) => (
              <Reveal key={step} delay={i * 0.08} className="bg-carbon">
                <div data-testid={`process-step-${i + 1}`} className="p-6 sm:p-8 group hover:bg-steel transition-colors duration-300">
                  <p className="font-mono text-xs text-lime">0{i + 1}</p>
                  <p className="mt-4 font-display font-bold text-lg sm:text-xl text-white group-hover:text-lime transition-colors">
                    {step}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <p className="mt-6 text-sm text-gray-500 max-w-2xl">
              Consultancy from concept to creation — followed by installation, commissioning
              and long-term operation of every plant we design.
            </p>
          </Reveal>
        </div>
      </section>

      {/* NET ZERO CLOCK */}
      <section data-testid="netzero-clock-section" className="py-20 sm:py-28 lg:py-32 border-b border-white/10 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal className="flex justify-center">
            <NetZeroClock />
          </Reveal>
          <Reveal delay={0.1}>
            <Eyebrow>The Net Zero clock</Eyebrow>
            <h2 className="mt-4 font-display font-bold tracking-tight text-3xl sm:text-4xl lg:text-5xl">
              Every year counts <span className="text-gradient">on the road to 2050</span>
            </h2>
            <Link
              to="/contact"
              data-testid="clock-cta-link"
              className="mt-8 inline-flex items-center gap-2 text-lime font-semibold hover:gap-3 transition-all duration-300"
            >
              Move the clock with us <ArrowRight size={18} />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* FOUNDER TEASER */}
      <section className="py-20 sm:py-28 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <Reveal className="relative">
            <div className="relative rounded-2xl border border-white/10 bg-gradient-to-br from-lime/10 via-carbon to-obsidian p-10 flex flex-col items-center text-center overflow-hidden">
              <img
                src="/assets/logo.jpeg"
                alt="Green Planet Technologies logo"
                className="logo-spin h-44 w-44 sm:h-56 sm:w-56 object-contain"
              />
              <p className="mt-7 font-mono text-[11px] uppercase tracking-[0.3em] text-lime">Promoter & Founder</p>
              <p className="font-display font-bold text-2xl text-white mt-1">Suresh C. Mirchandani</p>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <Eyebrow>Leadership</Eyebrow>
            <blockquote className="mt-6 font-display font-bold tracking-tight text-2xl sm:text-3xl lg:text-4xl leading-snug text-white">
              "Every waste stream is a resource waiting for the right engineering. Our job is to
              close the loop — <span className="text-gradient">profitably and permanently</span>."
            </blockquote>
            <p className="mt-6 text-gray-400 leading-relaxed">
              Deep expertise across metals, ores, minerals, steel, biomass energy, waste
              valorization, renewable generation & storage, and water & air treatment plants.
            </p>
            <Link
              to="/founder"
              data-testid="founder-teaser-link"
              className="mt-8 inline-flex items-center gap-2 text-lime font-semibold hover:gap-3 transition-all duration-300"
            >
              Meet the founder <ArrowRight size={18} />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* CAPACITY */}
      <section className="py-20 sm:py-28 bg-carbon border-y border-white/10 grain relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <Eyebrow>Plant capacity</Eyebrow>
            <h2 className="mt-4 font-display font-bold tracking-tight text-3xl sm:text-4xl max-w-2xl">
              Medium to large capacity plants, <span className="text-gradient">sized to your feedstock</span>
            </h2>
            <p className="mt-5 max-w-2xl text-gray-400 leading-relaxed">
              Every configuration is engineered around your feedstock, site and product offtake —
              the right capacity is defined together during concept engineering.
            </p>
          </Reveal>
          <div className="mt-12 grid md:grid-cols-3 gap-5">
            {CAPACITY.map((c, i) => (
              <Reveal key={c.title} delay={i * 0.08}>
                <div
                  data-testid={c.testid}
                  className="h-full rounded-2xl border border-white/10 bg-obsidian p-7 hover:border-lime/40 hover:-translate-y-1 transition-all duration-300"
                >
                  <c.icon className="text-lime" size={26} />
                  <h3 className="mt-5 font-display font-bold text-xl text-white">{c.title}</h3>
                  <p className="mt-2 text-sm text-gray-400 leading-relaxed">{c.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-24 sm:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(132,204,22,0.1),transparent_60%)]" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Reveal>
            <h2 className="font-display font-extrabold tracking-tight text-4xl sm:text-5xl lg:text-6xl">
              Ready to close <span className="text-gradient">the loop?</span>
            </h2>
            <p className="mt-6 text-gray-300 max-w-xl mx-auto">
              Tell us about your feedstock, site or ambition — we will engineer the Net Zero pathway.
            </p>
            <Link
              to="/contact"
              data-testid="home-bottom-cta"
              className="mt-10 inline-flex items-center gap-2 bg-lime text-obsidian font-semibold px-8 py-4 rounded-full hover:bg-lime-bright hover:shadow-[0_0_36px_rgba(132,204,22,0.5)] hover:-translate-y-0.5 transition-all duration-300"
            >
              Start a Project <ArrowUpRight size={18} />
            </Link>
            <a
              href="/assets/green-planet-profile.pdf"
              download="Green-Planet-Technologies-Profile.pdf"
              data-testid="home-download-profile"
              className="mt-10 ml-4 inline-flex items-center gap-2 border border-white/20 text-white font-medium px-8 py-4 rounded-full hover:border-lime/60 hover:text-lime transition-all duration-300"
            >
              Download Company Profile <ArrowRight size={18} />
            </a>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Home;
